import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";
import { isDefined } from "@omni/utils/helper.utils";
import React, {
    createContext,
    lazy,
    Suspense,
    use,
    useEffect,
    useRef,
    useState
} from "react";

import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { translateWithoutContext } from "@/redux/modules/value/translations/translations.helpers";
import BpostService, { ErrorField } from "@/services/bpost.service";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import {
    AddressToValidate,
    BusinessAddressToValidate
} from "@/utils/address/validation.types";

const ConfirmAddressModal = lazy(() =>
    import(
        "@/components/Address/context/AddressValidation/components/ConfirmAddressModal/confirmAddressModal.component"
    ).then((module) => ({ default: module.ConfirmAddressModal }))
);
const InvalidAddressModal = lazy(() =>
    import(
        "@/components/Address/context/AddressValidation/components/InvalidAddressModal/invalidAddressModal.component"
    ).then((module) => ({ default: module.InvalidAddressModal }))
);
const InvalidBusinessAddressModal = lazy(() =>
    import(
        "@/components/Address/context/AddressValidation/components/InvalidBusinessAddressModal/invalidBusinessAddressModal.component"
    ).then((module) => ({ default: module.InvalidBusinessAddressModal }))
);
const CreateOrEditAddressModal = lazy(() =>
    import(
        "@/components/Address/modals/CreateOrEditAddressModal/createOrEditAddressModal.component"
    ).then((module) => ({ default: module.CreateOrEditAddressModal }))
);

export type ValidationAddress = {
    id?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    street: string;
    number: string;
    zipCode: string;
    city: string;
    country: string;
    box?: string | null;
    addressing?: string | null;
    validationStatus: AddressValidationStatus;
};

export type AddressValidationProviderValues = {
    validateCheckout: (
        addressToValidate: AddressToValidate
    ) => Promise<AddressValidationStatus>;
    validate: (
        addressToValidate: AddressToValidate
    ) => Promise<AddressValidationStatus>;
    validateBusiness: (
        businessAddressToValidate: BusinessAddressToValidate
    ) => Promise<AddressValidationStatus>;
    isValidating: boolean;
    errorFields: Array<ErrorField>;
};

type AddressValidationProviderProps = {
    children?: React.ReactNode;
};

const AddressValidationContext =
    createContext<AddressValidationProviderValues | null>(null);

export const AddressValidationProvider = ({
    children
}: AddressValidationProviderProps) => {
    const promiseRef = useRef<
        ((status: AddressValidationStatus) => void) | null
    >(null);
    const checkoutPromiseRef = useRef<
        ((status: AddressValidationStatus) => void) | null
    >(null);

    const [isConfirmAddressModalOpen, setConfirmAddressModalOpen] =
        useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [errorFields, setErrorFields] = useState<Array<ErrorField>>([]);
    const [displayErrorFields, setdisplayErrorFields] = useState<
        Array<ErrorField>
    >([]);
    const [isEditAddressModalOpen, setIsEditAddresModalOpen] = useState(false);
    const [isInvalidAddressModalOpen, setIsInvalidAddresModalOpen] =
        useState(false);
    const [
        isInvalidBusinessAddressModalOpen,
        setIsInvalidBusinessAddresModalOpen
    ] = useState(false);
    const [address, setAddress] = useState<ValidationAddress | null>();
    const [type, setType] = useState<AddressType | null>(null);

    useEffect(() => {
        if (!isEditAddressModalOpen) {
            clearMessageById(BPOSTInvalidAddressMessageId);
        }
    }, [isEditAddressModalOpen]);

    const clearState = () => {
        setIsEditAddresModalOpen(false);
        setIsInvalidAddresModalOpen(false);
        setIsInvalidBusinessAddresModalOpen(false);
        setConfirmAddressModalOpen(false);
        setAddress(null);
        setType(null);
        setIsValidating(false);
    };

    // The one with edit + unknown ( confirm found )
    const validateCheckout = async ({
        id,
        firstName,
        lastName,
        street,
        number,
        zipCode,
        city,
        country,
        box,
        addressing,
        type,
        isBpostField,
        currentStatus
    }: AddressToValidate) => {
        return new Promise<AddressValidationStatus>((resolve) => {
            setErrorFields([]);
            setdisplayErrorFields([]);

            if (isBpostField) {
                return resolve(AddressValidationStatus.Validated);
            }

            if (country !== Country.Be) {
                return resolve(AddressValidationStatus.NoValidationNeeded);
            }

            const isValidStatus = [
                AddressValidationStatus.NoValidationNeeded,
                AddressValidationStatus.OverruledByCustomer
            ].some((s) => s === currentStatus);

            if (isValidStatus) {
                return resolve(currentStatus);
            }

            setIsValidating(true);
            BpostService.validateAddress({
                street,
                number,
                zipCode,
                city,
                box
            }).then((validationResult) => {
                setIsValidating(false);

                checkoutPromiseRef.current = resolve;

                setType(type || null);
                setErrorFields(validationResult.errorFields || []);

                if (
                    validationResult.isValid &&
                    currentStatus === AddressValidationStatus.Unknown
                ) {
                    setAddress({
                        id,
                        firstName,
                        lastName,
                        street,
                        number,
                        zipCode,
                        city,
                        country,
                        box,
                        addressing,
                        validationStatus: currentStatus
                    });
                    setConfirmAddressModalOpen(true);
                    return;
                }

                if (validationResult.isValid && validationResult.newStatus) {
                    return resolve(validationResult.newStatus);
                }

                setAddress({
                    id,
                    firstName,
                    lastName,
                    street,
                    number,
                    zipCode,
                    city,
                    box,
                    country,
                    addressing,
                    validationStatus: currentStatus
                });
                setdisplayErrorFields(validationResult.errorFields || []);
                broadcastMessage({
                    id: BPOSTInvalidAddressMessageId,
                    text: translateWithoutContext(
                        "bpost.validation.address.invalid"
                    ),
                    status: "warning",
                    dismissible: true
                });
                setIsEditAddresModalOpen(true);
            });
        });
    };

    // The one with just invalid (confirm)
    const validate = async ({
        id,
        firstName,
        lastName,
        street,
        number,
        zipCode,
        city,
        country,
        box,
        addressing,
        type,
        isBpostField,
        currentStatus
    }: AddressToValidate) => {
        return new Promise<AddressValidationStatus>((resolve) => {
            setErrorFields([]);
            setdisplayErrorFields([]);

            if (isBpostField) {
                return resolve(AddressValidationStatus.Validated);
            }

            if (country !== Country.Be) {
                return resolve(AddressValidationStatus.NoValidationNeeded);
            }

            setType(type || null);
            setIsValidating(true);

            BpostService.validateAddress({
                street,
                number,
                zipCode,
                city,
                box
            }).then((validationResult) => {
                setIsValidating(false);
                promiseRef.current = resolve;

                setErrorFields(validationResult.errorFields || []);

                if (validationResult.isValid && validationResult.newStatus) {
                    return resolve(validationResult.newStatus);
                }

                setIsInvalidAddresModalOpen(true);
                setAddress({
                    id,
                    firstName,
                    lastName,
                    street,
                    number,
                    zipCode,
                    city,
                    box,
                    country,
                    addressing,
                    validationStatus: currentStatus
                });

                resolve(currentStatus);
            });
        });
    };

    const validateBusiness = async ({
        companyName,
        street,
        number,
        zipCode,
        city,
        country,
        box,
        addressing,
        type,
        isBpostField,
        currentStatus
    }: BusinessAddressToValidate) => {
        return new Promise<AddressValidationStatus>((resolve) => {
            setErrorFields([]);
            setdisplayErrorFields([]);

            if (isBpostField) {
                return resolve(AddressValidationStatus.Validated);
            }

            if (country !== Country.Be) {
                return resolve(AddressValidationStatus.NoValidationNeeded);
            }

            setType(type || null);
            setIsValidating(true);

            BpostService.validateAddress({
                street,
                number,
                zipCode,
                city,
                box
            }).then((validationResult) => {
                setIsValidating(false);
                promiseRef.current = resolve;

                setErrorFields(validationResult.errorFields || []);

                if (validationResult.isValid && validationResult.newStatus) {
                    return resolve(validationResult.newStatus);
                }

                setIsInvalidBusinessAddresModalOpen(true);
                setAddress({
                    companyName,
                    street,
                    number,
                    zipCode,
                    city,
                    box,
                    country,
                    addressing,
                    validationStatus: currentStatus
                });

                resolve(currentStatus);
            });
        });
    };

    return (
        <AddressValidationContext
            value={{
                validate,
                validateCheckout,
                validateBusiness,
                isValidating,
                errorFields: displayErrorFields
            }}
        >
            <Suspense>
                <ConfirmAddressModal
                    isOpen={isConfirmAddressModalOpen}
                    address={address}
                    onSubmit={() => {
                        clearState();
                        setErrorFields([]);
                        setdisplayErrorFields([]);
                        checkoutPromiseRef.current?.(
                            AddressValidationStatus.Validated
                        );
                    }}
                    type={type}
                    onClose={() => {
                        setErrorFields([]);
                        setdisplayErrorFields([]);
                        clearState();
                        checkoutPromiseRef.current?.(
                            AddressValidationStatus.Closed
                        );
                    }}
                    onEdit={() => {
                        setdisplayErrorFields(errorFields);
                        setIsEditAddresModalOpen(true);
                        setConfirmAddressModalOpen(false);
                    }}
                />
                <InvalidAddressModal
                    type={type}
                    address={address}
                    isOpen={isInvalidAddressModalOpen}
                    onEdit={() => {
                        setdisplayErrorFields(errorFields);
                        setIsInvalidAddresModalOpen(false);
                    }}
                    onClose={() => {
                        if (isEditAddressModalOpen) {
                            setIsInvalidAddresModalOpen(false);
                        } else {
                            setErrorFields([]);
                            setdisplayErrorFields([]);
                            clearState();
                        }
                        promiseRef.current?.(AddressValidationStatus.Closed);
                    }}
                    onConfirm={() => {
                        clearState();
                        setErrorFields([]);
                        setdisplayErrorFields([]);
                        promiseRef.current?.(
                            AddressValidationStatus.OverruledByCustomer
                        );
                    }}
                />
                <InvalidBusinessAddressModal
                    type={type}
                    address={address}
                    isOpen={isInvalidBusinessAddressModalOpen}
                    onEdit={() => {
                        setdisplayErrorFields(errorFields);
                        setIsInvalidBusinessAddresModalOpen(false);
                    }}
                    onClose={() => {
                        if (isEditAddressModalOpen) {
                            setIsInvalidBusinessAddresModalOpen(false);
                        } else {
                            setErrorFields([]);
                            setdisplayErrorFields([]);
                            clearState();
                        }
                        promiseRef.current?.(AddressValidationStatus.Closed);
                    }}
                    onConfirm={() => {
                        clearState();
                        setErrorFields([]);
                        setdisplayErrorFields([]);
                        promiseRef.current?.(
                            AddressValidationStatus.OverruledByCustomer
                        );
                    }}
                />
                <CreateOrEditAddressModal
                    address={address}
                    open={isEditAddressModalOpen}
                    type={type}
                    skipValidation={false}
                    onChange={() => {
                        clearState();
                        setErrorFields([]);
                        setdisplayErrorFields([]);
                        checkoutPromiseRef.current?.(
                            AddressValidationStatus.Validated
                        );
                    }}
                    setOpen={() => {
                        setErrorFields([]);
                        setdisplayErrorFields([]);
                        clearState();
                        checkoutPromiseRef.current?.(
                            AddressValidationStatus.Closed
                        );
                    }}
                />
            </Suspense>
            {children}
        </AddressValidationContext>
    );
};

export const useAddressValidationContext = () => {
    const context = use(AddressValidationContext);

    if (!isDefined(context)) {
        throw new Error(
            "useAddressValidationContext can only be used inside AddressValidationProvider"
        );
    }

    return context;
};
